import FormProcessHandler from 'root/main/components/forms/core/formProcessHandler';
import Events from 'root/helpers/events';
import { BrowserHistoryCtl } from 'root/utils/dom/browserHistoryCtl';
import { CORS } from 'root/utils/cors/cors';
import { Lexicon } from 'root/helpers/lexicon';
import LoadingIndicator from 'root/helpers/loadingIndicator';
import { LoggingHandler } from 'root/utils/errorReporting/LoggingHandler';
import { CookieMonster } from 'root/utils/cookies/cookieMonster';
import { hashedEmail } from 'root/helpers/getHashedEmail';
import AffiliatesPixel from 'root/core/tracking/affiliatesPixel';
import { gtm } from 'root/utils/tracking/googleTagManagerDeprecated';
import { getFieldName } from 'root/utils/forms/getFieldData';
import {
    storeEncodedFormFieldValue,
    isFormExcludedFromLocalStorage,
    isEnabledFormAutofill,
} from 'root/utils/dom/localStorage';
import { replaceAllMenuTemplateAssetUrlsAndDownload } from 'root/main/components/menuTemplate/menuTemplate';
import { populateWebinarIdField } from 'root/main/components/events/eventCard';
import { capitalize } from 'root/utils/string/capitalize';
import {
    sendWizardFormElementEventToGTM,
    isAgnosticWizardForm,
} from 'root/main/components/wizardForm/wizardFormGTM';
import { setRestaurantProductInHiddenFormField } from 'root/core/restaurantProductTracking/restaurantProductTracking';
import { FormGTMObjectConstructor } from 'root/utils/tracking/GA4/formTracking';
import { GoogleTagManager } from 'root/utils/tracking/GA4/googleTagManager';

/**
 * FormController, controls the data flow of the forms submission
 *
 * // @typedef {object} FormController
 *
 * @param {FormContext} context the form current context
 * @returns {FormController} FormController
 */
const FormController = function (context) {
    this.context = context;
    this.form = this.context.getForm();
};

FormController.prototype = {
    originalAddressInput: '',
    endpoint: '/rest/forms/master-submit',
    httpMethod: 'POST',
    /**
     * Process to be executed on form submit success
     * the Order of the process added to the bin MATTERS. all processes are added to the top of the array. so last added first executed
     *
     * @type {array}
     */
    successProcessBin: [],
    /**
     * Process to be executed on form submit error
     * the Order of the process added to the bin MATTERS. all processes are added to the top of the array. so last added first executed
     *
     * @type {array}
     */
    errorsProcessBin: [],
    /**
     * Process to be executed on all form submit after success/error
     * the Order of the process added to the bin MATTERS. all processes are added to the top of the array. so last added first executed
     *
     * NOTE: the process `redirect_user` needs to be the last process on the always Bin
     *
     * @type {array}
     */
    alwaysProcessBin: [
        {
            name: 'redirect_user',
            fnc: (name, context, next) => {
                const resp = context.getResponse();
                resp && resp.url
                    ? document.location.replace(resp.url)
                    : FormController.prototype.httpRequestErrorHandler(resp);

                return next;
            },
        },
    ],

    /**
     * addSuccessProcess
     * The process function needs to call the `next` function when ready. this can be async or not.
     *
     * @param {string} name the name of the process
     * @param {function} process function to be executed. the argument passed to the function are `name`, `FormContext`, `next`
     */
    addSuccessProcess(name, process) {
        this.addProcess(name, process, this.successProcessBin);
    },

    /**
     * addAlwaysProcess
     * The process function needs to call the `next` function when ready. this can be async or not.
     *
     * @param {string} name the name of the process
     * @param {function} process function to be executed. the argument passed to the function are `name`, `FormContext`, `next`
     */
    addAlwaysProcess(name, process) {
        this.addProcess(name, process, this.alwaysProcessBin);
    },

    /**
     * addErrorProcess
     * The process function needs to call the `next` function when ready. this can be async or not.
     *
     * @param {string} name the name of the process
     * @param {function} process function to be executed. the argument passed to the function are `name`, `FormContext`, `next`
     */
    addErrorProcess(name, process) {
        this.addProcess(name, process, this.errorsProcessBin);
    },

    /**
     * addProcess, does an Array.shift on the bin. All Process are stacked, so last in first executed
     *
     * NOTE: this method should not be called directly please use `addSuccessProcess|addErrorProcess|addAlwaysProcess` methods
     *
     * @param {string} name name
     * @param {function} process process
     * @param {array} processesBin The bin to add the process to
     */
    addProcess(name, process, processesBin) {
        if (typeof process !== 'function') {
            const type = typeof process;
            throw new Error(`the process: ${process} is not a function, ${type} given`);
        }

        if (name in processesBin) {
            console.warn('overriding process named: ' + name);
        }

        processesBin.unshift({ name: name, fnc: process });
    },

    /**
     * doProcesses, initiate the processes of a bin
     *
     * @param {array} bin Process Bin
     */
    doProcesses(bin) {
        const processHandler = new FormProcessHandler(bin, this);
        const next = processHandler.getNext();
        next();
    },

    getToken(callback) {
        CORS.xdr('/rest/forms/load/token', 'GET', '', callback, (error, status) => {
            this.httpRequestErrorHandler({ error, status });
            this.doProcesses(this.errorsProcessBin);
            // this.doProcesses(this.alwaysProcessBin);
        });
    },

    isRedirectOnSubmit() {
        return this.form.hasAttribute('data-redirect-on-submit');
    },

    /* we are saving the address field value before it gets re-assigned to a value in the prototype
                so we can re-populate the address field with the original value in case the submission was not successful
                This value will be reassigned to the value of the address field on error inside this function resetAddressFieldOnError()
                 */
    setOriginalAddressValue(field, fieldName) {
        if (fieldName === 'address') {
            this.originalAddressInput = field.value;
        }
    },
    /**
     * Here we are not targeting the hidden fields because the fields we will be targeting are not yet hidden fields, they have not been included in the form configs hidden_fields section. For this reason we are reassigning the data attribute values to the value of the form field, so that once it is transformed to a hidden field it will be output with the desired value.
     * @param field
     * @param fieldName
     */
    changeHiddenFieldValue(field, fieldName) {
        if (field.name === fieldName) {
            if (field.name === 'zip' && field.dataset.googlePlacesField !== 'true') {
                return;
            }
            const dataAttributeValue = field.dataset[`leadform${capitalize(fieldName)}`];
            this.setOriginalAddressValue(field, fieldName);
            field.value = dataAttributeValue;
        }
    },

    resetAddressFieldOnError() {
        const fields = this.context.getAllFormFields();
        const addressField = Array.from(fields).find(field => field.name === 'address');
        if (addressField) addressField.value = this.originalAddressInput;
    },

    changeHiddenFieldsValueOnSubmit() {
        const fields = this.context.getAllFormFields();
        fields.forEach(field => {
            this.changeHiddenFieldValue(field, 'zip');
            this.changeHiddenFieldValue(field, 'address');
        });
    },

    sendOptinEventToGTM(formGTMObjectConstructor) {
        const optin = this.context.getFields()['general_opt_in'];
        if (optin) {
            // check for optin whether it has been checked or not
            if (optin.toString() === '1') {
                gtm.form(this.form).sendEvent('marketing-opt-in');
                GoogleTagManager.sendEvent(
                    formGTMObjectConstructor.getFormGTMObject('form_email_opt_in')
                );
            } else {
                gtm.form(this.form).sendEvent('marketing-opt-out');
                GoogleTagManager.sendEvent(
                    formGTMObjectConstructor.getFormGTMObject('form_email_opt_out')
                );
            }
        }
    },

    sendEventsToGoogleTagManagerOnSubmit(formGTMObjectConstructor) {
        /*
         * START fix for Wizard form AB test tracking
         *  */
        if (isAgnosticWizardForm(this.form)) {
            const optin = this.context.getFields()['general_opt_in'];
            if (optin) {
                if (optin.toString() === '1') {
                    sendWizardFormElementEventToGTM(this.form, 'marketing-opt-in');
                    GoogleTagManager.sendEvent(
                        formGTMObjectConstructor.getFormGTMObject('form_email_opt_in')
                    );
                } else {
                    sendWizardFormElementEventToGTM(this.form, 'marketing-opt-out');
                    GoogleTagManager.sendEvent(
                        formGTMObjectConstructor.getFormGTMObject('form_email_opt_out')
                    );
                }
            }
            sendWizardFormElementEventToGTM(this.form, 'submit');
            /*
             * END of fix for Wizard form AB test tracking
             *  */
        } else {
            this.sendOptinEventToGTM(formGTMObjectConstructor);
            // Once the form submit button has been clicked
            gtm.form(this.form).sendEvent('submit');
        }

        const isNotWizardForm = !this.form.dataset.formName.includes('wizard');
        if (isNotWizardForm) {
            /* only track non-wizard forms, because wizard forms are tracked separately in wizardForm.js */
            GoogleTagManager.sendEvent(
                formGTMObjectConstructor.getFormGTMObject('form_submit')
            );
        }
    },

    /**
     * handler for all form submission
     *
     * @param {object} e Form submission event
     */
    handler(e) {
        e.preventDefault();
        Events.fire(this.form, 'leadform_submit');
        const formGTMObjectConstructor = new FormGTMObjectConstructor(this.form);
        this.sendEventsToGoogleTagManagerOnSubmit(formGTMObjectConstructor);
        this.context.setRequest(e);
        this.changeHiddenFieldsValueOnSubmit();
        this.showLoadingAnimation();
        this.hideFormOnSubmit();
        // Set restaurant product series (conditional logic is in function)
        setRestaurantProductInHiddenFormField(this.form);

        // add the token
        this.getToken(token => {
            CORS.xdr(
                this.endpoint,
                this.httpMethod,
                `${this.context.getSerializedFields()}&token=${encodeURIComponent(
                    token
                )}`,
                (success, status) => {
                    BrowserHistoryCtl.addCurrentPage();
                    this.context.setResponse(this.jsonDecode(success));
                    if (this.context.getResponse() === null) {
                        //TODO we must implement a fallback in case there was an error this is bad for the user.
                        this.httpRequestErrorHandler({ success, status });
                        return;
                    }
                    this.doProcesses(this.successProcessBin);
                    if (this.isRedirectOnSubmit()) {
                        this.doProcesses(this.alwaysProcessBin); //TODO duplicate with the error function since there is no always function on the CORS object
                    }
                    this.hideLoadingAnimation();
                    this.showSuccessMessage();
                },
                (error, status) => {
                    this.context.setResponse(this.jsonDecode(error));
                    if (this.context.getResponse() === null) {
                        // If the status is 429 - rate limiting, it will go down this path
                        this.httpRequestErrorHandler({ error, status });
                        return;
                    }
                    this.doProcesses(this.errorsProcessBin);
                    this.resetAddressFieldOnError();
                    if (this.isRedirectOnSubmit()) {
                        //If the response is 400 - email already in use, it will go down this path
                        this.doProcesses(this.alwaysProcessBin); //TODO duplicate with the success function since there is no always function on the CORS object
                    }
                }
            );
        });
    },

    /**
     * httpRequestErrorHandler, if not errors or invalid json was returned form the form submit
     * then display something defined in the lexic or a default message
     *
     * @param {object|null} response data
     */
    httpRequestErrorHandler(response) {
        try {
            Lexicon.get('forms/form_error_feedback', lexicon => {
                this.errorMessageCallback(response, lexicon);
            });
        } catch {
            this.errorMessageCallback(response);
        }
    },

    /**
     * @returns {string}
     * @param response
     * @param lexicon
     */
    errorMessageCallback(response, lexicon = {}) {
        const errorMsg = this.getErrorMessageFromLexic(response, lexicon);
        this.displayErrorMessage({ errors: [errorMsg] }, lexicon);
        LoggingHandler().logError(response, {
            searchTerm: 'lexic-error-message',
            tags: ['forms', 'ui'],
            fileName: 'formController.js',
            additionalData: {
                errorMsg,
                response,
            },
        });
    },

    /**
     * @returns {string}
     * @param response
     * @param lexicon
     */
    getErrorMessageFromLexic(response, lexicon = {}) {
        const status = response.status ? parseInt(response.status) : null;
        const errorMessages = lexicon['error_messages'] ?? [];
        switch (status) {
            case 429:
                return (
                    this.getStatusCodeMessage(429, errorMessages) ??
                    `You have attempted too many form submissions. Please try again in 10 minutes. (Response code 429 - rate limiting)`
                );
            case status >= 400:
                return (
                    this.getStatusCodeMessage('default', errorMessages) ??
                    'Internal error, we apologize.'
                );
            case null:
                // when the email is already in use, the status is null and the response.errors is an array
                return (
                    response.errors ??
                    this.getStatusCodeMessage('default', errorMessages) ??
                    'Internal error, we apologize.'
                );
            default:
                return (
                    this.getStatusCodeMessage('default', errorMessages) ??
                    'Internal error, we apologize.'
                );
        }
    },

    /**
     * @returns {string}
     * @param statusCode
     * @param errorMessages
     */
    getStatusCodeMessage(statusCode, errorMessages) {
        const errorMessage = errorMessages.find(
            error => error['status_code'] === statusCode.toString()
        );

        return errorMessage?.message ?? '';
    },

    /**
     * jsonDecode, wraps JSON.parse in a try/catch
     * @param {string} json json string @return {json}
     */
    jsonDecode(json) {
        try {
            return JSON.parse(json);
        } catch (e) {
            return null;
        }
    },

    /**
     * @param response
     * @param lexicon
     */
    displayErrorMessage(response, lexicon) {
        // This creates a list item in the DOM and inserts error messages in it
        if (response !== undefined && Array.isArray(response.errors)) {
            const errorList = document.querySelector(
                '[data-loader="ls-loader-error-list"]'
            );
            if (errorList) {
                if (response.errors.length === 1) {
                    errorList.style.listStyle = 'none';
                }
                // Clean the error list - otherwise multiple tries keep adding to el
                while (errorList.firstChild) {
                    errorList.removeChild(errorList.firstChild);
                }
                response.errors.forEach(function (error) {
                    const li = document.createElement('li');
                    if (
                        error === '' ||
                        error === null ||
                        (Array.isArray(error) && error.length === 0)
                    ) {
                        error = 'unknown';
                    }
                    if (error === 'Email address invalid.') {
                        const emailInUse =
                            (lexicon && lexicon['email_in_use']) ||
                            'Email already in use.';
                        const loginPrompt =
                            (lexicon && lexicon['login_prompt']) || 'Log In Here';
                        error = `${emailInUse} <a href="https://cloud.lightspeedapp.com/login.html">${loginPrompt}</a>`;
                    }
                    li.innerHTML = error;
                    errorList.appendChild(li);
                });
            }
        }
        LoadingIndicator().showError();
    },

    /**
     * showLoadingAnimation, starts the FormEventFeedbackCtl
     *
     */
    showLoadingAnimation() {
        if (this.form.hasAttribute('data-show-loader-on-submit')) {
            LoadingIndicator({ form: this.form }).start();
        }
    },

    /**
     * hideLoadingAnimation, hides the Loading Animation on forms that don't trigger a redirect after submit
     *
     */
    hideLoadingAnimation() {
        if (
            this.form.hasAttribute('data-show-loader-on-submit') &&
            !this.isRedirectOnSubmit()
        ) {
            LoadingIndicator({ form: this.form }).hide();
        }
    },

    /**
     * showSuccessMessage, show success message on forms that don't trigger a redirect after submit
     *
     */
    showSuccessMessage() {
        if (
            this.form.hasAttribute('data-show-loader-on-submit') &&
            !this.isRedirectOnSubmit()
        ) {
            LoadingIndicator({ form: this.form }).showSuccessMessage();
        }
    },

    /**
     * hideFormOnSubmit, hide the form so we can see the FormEventFeecback
     *
     */
    hideFormOnSubmit() {
        if (this.form.hasAttribute('data-hide-form-on-submit')) {
            const formSection = this.form.closest('[data-form=form-section]');
            const contentSection = this.form.closest('[data-form=form-content]');
            const leadformSection = this.form.closest('.leadform');
            if (contentSection) {
                contentSection.style.display = 'none';
            }
            if (formSection) {
                formSection.style.display = 'none';
            }
            if (
                leadformSection &&
                leadformSection.classList.contains('gb-section-spacing')
            ) {
                leadformSection.classList.remove('gb-section-spacing');
            }
        }
    },

    /**
     * init, adds the event listener to the form
     *
     */
    init() {
        Events.removeListenerType('lightboxOpened');
        this.form.addEventListener('submit', this.handler.bind(this));
        this.form.form_type && this.form.form_type.value === 'default'
            ? gtm.form(this.form).sendEvent('form-load')
            : false;

        if (this.form.querySelector('input[name="webinar_id"]')) {
            populateWebinarIdField();
        }
    },
};

//-----------------------------------------------------------------------------------------
//------------------------- ADD PROCESSES BELOW -------------------------------------------
//-----------------------------------------------------------------------------------------

// IMPORTANT: THIS FUNCTION TRACKS THE SUCCESSFUL SUBMISSION OF THE FORM //
//-------------------------------------------------------------------------------------------//
FormController.prototype.addSuccessProcess('gtm', (name, context, next) => {
    const formGTMObjectConstructor = new FormGTMObjectConstructor(context.getForm());
    GoogleTagManager.sendEvent(formGTMObjectConstructor.getFormGTMObject('form_success'));
    // Fix for the Wizard form A/B test tracking
    if (isAgnosticWizardForm(context.getForm())) {
        sendWizardFormElementEventToGTM(context.getForm(), 'success');
        // END of exception for Wizard form A/B test
    } else {
        gtm.form(context.getForm()).sendEvent('success');
    }
    next();
});

FormController.prototype.addSuccessProcess('datadog', (name, context, next) => {
    const isTestSubmission =
        context.getFields().email?.includes('lightspeedhq.com') ?? false;
    LoggingHandler().logUserAction(
        `User successfully submitted the ${context.getFields().form_name} form`,
        {
            searchTerm: 'successful-form-submission',
            tags: ['forms'],
            fileName: 'formController.js',
            additionalData: {
                isTestSubmission: isTestSubmission,
            },
        }
    );
    next();
});

FormController.prototype.addSuccessProcess('localStorage', (name, context, next) => {
    if (!isFormExcludedFromLocalStorage(context.getForm()) && isEnabledFormAutofill()) {
        context
            .getAllFormFields()
            .forEach(field => storeEncodedFormFieldValue(field, getFieldName(field)));
    }
    next();
});

FormController.prototype.addSuccessProcess('gauserid', (name, context, next) => {
    window.registerCookieComplianceCallback({
        callback: () => {
            const cm = new CookieMonster();
            const email = context.getForm().email.value;
            if (email) {
                return hashedEmail.get(email, userId => {
                    cm.setCookie('GAUSERID', userId, 1);
                });
            }
        },
        category: 'targeting',
        isCallOptanonWrapper: true,
    });
    next();
});

FormController.prototype.addErrorProcess('gtm', (name, context, next) => {
    gtm.form(context.getForm()).sendEvent('error');
    if (context.getForm() instanceof HTMLFormElement) {
        const formGTMObjectConstructor = new FormGTMObjectConstructor(context.getForm());
        GoogleTagManager.sendEvent(
            formGTMObjectConstructor.getFormGTMObject('form_error')
        );
    } else {
        GoogleTagManager.sendEvent({
            event_action: 'form_error',
            form_name: 'unknown',
            product_type: 'unknown',
            stage: 'unknown',
        });
    }
    next();
});

FormController.prototype.addErrorProcess('datadog', (name, context, next) => {
    const isTestSubmission =
        context.getFields().email?.includes('lightspeedhq.com') ?? false;
    LoggingHandler().logError(
        `User unsuccessfully submitted ${context.getFields().form_name} form`,
        {
            searchTerm: 'failed-form-submission',
            tags: ['forms'],
            fileName: 'formController.js',
            additionalData: {
                isTestSubmission: isTestSubmission,
                formError: context.getResponse(),
            },
        }
    );
    next();
});
//-------------------------------------------------------------------------------------------//

FormController.prototype.addSuccessProcess('affiliate', (name, context, next) => {
    try {
        const affiliate = new AffiliatesPixel(new CookieMonster());
        affiliate.fire(() => {
            next();
        });
    } catch (e) {
        next();
    }
});

// @todo refactor this function to make it more scalable and DRY (as we might be adding more tools).
FormController.prototype.addSuccessProcess('ls-tool', (name, context, next) => {
    if (
        context.getForm().dataset.formName === 'restaurant_resources_tool_menu_templates'
    ) {
        replaceAllMenuTemplateAssetUrlsAndDownload();
    }
    if (
        context.getForm().dataset.formName === 'retail_resources_tool_barcode_generator'
    ) {
        const els = document.querySelectorAll('[data-barcode-generator]');
        if (els && els.length > 0) {
            els.forEach(el => {
                if (el) {
                    import(
                        /*webpackChunkName: "barcodeGenerator"*/ 'root/main/components/barcodeGenerator/barcodeGenerator'
                    ).then(module =>
                        module
                            .barcodeGenerator(el)
                            .postSuccessProcess(true, context.getForm().email.value)
                    );
                }
            });
        }
    }
    next();
});

export { FormController as default };
